import LocalStorageService from '../../../utils/local-storage-service';
import ZApiClient from '../../../utils/client/z-api-client';
import { ZApiClient as Z4ApiClient, Environment } from '@marageti/z4-sdk/lib/client';
import { PartnerStatus } from '@marageti/z4-sdk/lib/travel';
import {
  getEffectivePublished,
  validatePartnerPublishState,
  syncPartnerPublished,
  UNPUBLISHED_STATUSES,
  isStatusEligibleForPublish,
} from './company-publish';

export {
  getEffectivePublished,
  validatePartnerPublishState,
  UNPUBLISHED_STATUSES,
  isStatusEligibleForPublish,
};

const environments = {
  dev: Environment.dev,
  staging: Environment.staging,
  qa: Environment.qa,
  prod: Environment.prod,
};

const localStorage = new LocalStorageService();
const env = process.env.REACT_APP_STAGE ? environments[process.env.REACT_APP_STAGE] : Environment.staging;
const z4ApiClient = new Z4ApiClient(env, localStorage.getFromLocalStorage('accessToken') || '');

const companySearchClient = ZApiClient.getCompanySearchClinet();

// Note: getCountryCodeToNameMap has been moved to LocationsStore context
// Use useLocationsStore().getCountryCodeToNameMap() in React components
// This function is kept for backward compatibility but should be deprecated
export const getCountryCodeToNameMap = async () => {
  const countries = await ZApiClient
    .getGeoNamesClient()
    .getCountries({ sorted: true, fields: ['country,isoCode'] });
  
  return countries.reduce((map, country) => {
    if (country.isoCode && country.country) {
      map[country.isoCode] = country.country;
    }
    return map;
  }, {});
};

// Helper to format country name for display
export const formatCountryName = (countryCode, countryCodeToNameMap = {}) => {
  return countryCodeToNameMap[countryCode] || countryCode;
};

// Helper to format date as YYYY-MM-DD
export const formatDate = (date) => {
  if (!date) return '';
  const d = new Date(date);
  if (isNaN(d.getTime())) return '';
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// User-facing labels for each Z4 PartnerStatus. Single source of truth — also consumed
// by companies-view and company-view for the status badge.
export const statusLabelMap = {
  [PartnerStatus.NEW]: 'New',
  [PartnerStatus.ONBOARDING]: 'Onboarding',
  [PartnerStatus.ACTIVE]: 'Active',
  [PartnerStatus.NON_STRATEGIC]: 'Non-Strategic',
  [PartnerStatus.PHASING_OUT]: 'Phasing Out',
  [PartnerStatus.INACTIVE]: 'Inactive',
};

// TODO(activity-level-removal): delete this constant, the legacyActivityLevelsToStatuses
// helper, and every import of it in the follow-up cleanup PR after release verification.
// One-shot migration shim: convert any persisted legacy `activityLevels` filter into the
// new `statuses` filter on load. Mirrors the server-side mapActivityLevelToStatus.
const LEGACY_ACTIVITY_LEVEL_TO_STATUS = {
  PRIORITY_MATCH: PartnerStatus.ACTIVE,
  SECOND_MATCH: PartnerStatus.NON_STRATEGIC,
  THIRD_OPTION: PartnerStatus.NON_STRATEGIC,
  VIP_HIGH_BUDGET: PartnerStatus.NON_STRATEGIC,
  REPEAT: PartnerStatus.NON_STRATEGIC,
  STANDARD: PartnerStatus.NON_STRATEGIC,
  LAST_RESORT: PartnerStatus.PHASING_OUT,
  LOW_END: PartnerStatus.PHASING_OUT,
  DO_NOT_MATCH: PartnerStatus.PHASING_OUT,
  ON_HOLD: PartnerStatus.PHASING_OUT,
  NEW: PartnerStatus.NEW,
  EXCLUDED: PartnerStatus.INACTIVE,
};

/**
 * Translate a legacy `activityLevels` array (as stored in older saved searches) into
 * the new `statuses` array. Unknown values are ignored. Preserves order, deduplicates.
 */
export const legacyActivityLevelsToStatuses = (activityLevels) => {
  if (!Array.isArray(activityLevels) || activityLevels.length === 0) return [];
  const seen = new Set();
  const result = [];
  for (const level of activityLevels) {
    const status = LEGACY_ACTIVITY_LEVEL_TO_STATUS[level];
    if (status && !seen.has(status)) {
      seen.add(status);
      result.push(status);
    }
  }
  return result;
};

export const defaultSearch = {
  filters: {
    statuses: [PartnerStatus.ACTIVE, PartnerStatus.NON_STRATEGIC, PartnerStatus.PHASING_OUT],
  },
  sortOrders: [
    {
      field: 'updatedAt',
      order: 'DESC',
    }
  ],
};

export const sortMap = {
  relevance: 'Relavance',
  newToOld: 'New to Old',
  oldToNew: 'Old to New',
  nameAToZ: 'Name A to Z',
  nameZToA: 'Name Z to A',
}

export const sortOrderOptions = {
  newToOld: {
    field: 'updatedAt',
    order: 'DESC',
  },
  oldToNew: {
    field: 'updatedAt',
    order: 'ASC',
  },
  nameAToZ: {
    field: 'name',
    order: 'ASC',
  },
  nameZToA: {
    field: 'name',
    order: 'DESC',
  }
};

const unauthorizedRedirect = (destination) => {
  const dest = destination ? `?destination=/${destination}` : '';
  window.location.href = `/login${dest}`;
};

export const fetchCompany = async (id) => {
  const apiClient = getZ4ApiClient();
  const partner = await apiClient.partnersClient.getPartner(id);
  
  if ('httpStatus' in partner) {
    if (partner.httpStatus === 401) unauthorizedRedirect(`companies/${id}`);
    throw new Error('Error fetching company');
  }
  
  return partner;
};

export const createCompanySearch = (params) => (
  companySearchClient.createCompanySearch(params).then((res) => {
    if ('httpStatus' in res) {
      if (res.httpStatus === 401) unauthorizedRedirect('companies');
    }
    return res;
  })
);

export const fetchCompanySearch = (id, offset) => (
  companySearchClient.readCompanySearch(
    id,
    {
      ...offset && { offset },
      limit: 50,
    },
  )
    .then((res) => {
      if ('httpStatus' in res) {
        if (res.httpStatus === 401) unauthorizedRedirect(`companies?search=${id}`);
        throw new Error('Error fetching company search');
      }
      return res;
    })
);

export const mapCompanyArrayToObject = (companies) => {
  const arrayByIds = {};
  for (let i = 0; i < companies.length; i += 1) {
    const company = companies[i];
    arrayByIds[company.id] = company;
  }
  return arrayByIds;
};

// Create PartnerUpdate from Partner. Syncs published from firstName + status so clearing
// the TC name unpublishes the profile (same rules as getEffectivePublished / Company.getPublished).
export const createPartnerUpdate = (partner, updates = {}) => {
  partner.firstName = updates.firstName !== undefined ? updates.firstName : (partner.firstName || '');
  partner.description = updates.description !== undefined ? updates.description : (partner.description || '');
  partner.photo = updates.photo !== undefined ? updates.photo : (partner.photo || '');
  return syncPartnerPublished(partner);
};

// Helper to format countries for display (converts PartnerCountry[] to display format)
// Accepts countryCodeToNameMap as second parameter to avoid fetching if already available
export const formatCountriesForDisplay = async (countries, countryCodeToNameMap = null) => {
  if (!countries || countries.length === 0) return [];
  
  // If map is not provided, fetch it (for backward compatibility)
  const countryMap = countryCodeToNameMap || await getCountryCodeToNameMap();
  return countries
    .filter(c => c && c.country)
    .map(c => ({
      id: c.country,
      name: formatCountryName(c.country, countryMap),
      isoCode: c.country,
      minBudgetPPPD: c.minBudgetPPPD,
    }));
};

const getZ4ApiClient = () => {
  const token = localStorage.getFromLocalStorage('accessToken') || '';
  if (z4ApiClient.instance.getAuthToken() !== token) {
    z4ApiClient.instance.setAuthToken(token);
  }
  return z4ApiClient;
};

export const saveCompany = (partner, updates = {}) => {
  const apiClient = getZ4ApiClient();
  const partnerUpdate = createPartnerUpdate(partner, updates);
  return apiClient.partnersClient.putPartner(partner.id, partnerUpdate)
    .then((updatedPartner) => {
      if ('httpStatus' in updatedPartner) {
        throw new Error('Error saving company');
      }
      return updatedPartner;
    });
};

export const createCompany = (partnerRequest) => {
  const apiClient = getZ4ApiClient();
  return apiClient.partnersClient.postPartner(partnerRequest)
    .then((partner) => {
      if ('httpStatus' in partner) {
        throw new Error('Error creating company');
      }
      return partner;
    });
};

/**
 * 
 * @param sortOption 
 *  {
 *    field: 'updatedAt', fistName
 *  }
 * @returns string
 */
export const getCompanySortValue = (sortOption) => {
  let res = 'relevance';
  if (sortOption) {
    for (const [sortValue, sortObject] of Object.entries(sortOrderOptions)) {
      if (sortOption.field === sortObject.field && sortOption.order === sortObject.order) {
        res = sortValue;
      }
    }
  }
  return res;
}

export const getCompanySortOrders = (sortOrder) => {
  const res = [];
  if (sortOrder !== 'relevance') {
    res.push(sortOrderOptions[sortOrder]);
  }
  return res;
}

/**
 * Filter companies by fields not indexed in Z4 (firstName, photo, description).
 * Per-page results are post-filtered after the search returns.
 */
export const applyClientSideCompanyFilters = (companies, filters) => {
  if (!Array.isArray(companies)) return [];
  const firstNameExists = filters?.firstNameExists;
  const photoExists = filters?.photoExists;
  const descriptionExists = filters?.descriptionExists;
  let result = companies;
  if (typeof firstNameExists === 'boolean') {
    result = result.filter(
      (c) => Boolean(c?.firstName && c.firstName.trim().length > 0) === firstNameExists,
    );
  }
  if (typeof photoExists === 'boolean') {
    result = result.filter((c) => Boolean(c?.photo?.uri) === photoExists);
  }
  if (typeof descriptionExists === 'boolean') {
    result = result.filter(
      (c) => Boolean(c?.description && c.description.trim().length > 0) === descriptionExists,
    );
  }
  return result;
};

export const hasClientSideCompanyFilters = (filters) =>
  typeof filters?.firstNameExists === 'boolean'
  || typeof filters?.photoExists === 'boolean'
  || typeof filters?.descriptionExists === 'boolean';
